import { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import {
  type EcosystemLocale,
  type EcosystemPagePayload,
  type EcosystemSectionInput,
  type EcosystemFactInput,
  type EcosystemFaqInput,
  type InfoMarkerItemInput,
  type InfoMarkerPayload,
  getAdminEcosystemMarkers,
  getAdminEcosystemPage,
  publishAdminEcosystem,
  saveAdminEcosystemMarkers,
  saveAdminEcosystemPage,
} from "../api/adminEcosystemApi";
import RichTextEditor from "../Components/ui/RichTextEditor";

type AdminTabKey = "overview" | "categories" | "faq" | "markers" | "preview";

type TopicOption = {
  key: string;
  label: string;
};

const EMPTY_SECTION: EcosystemSectionInput = {
  id: "",
  title: "",
  summary: "",
  body: "",
  highlights: [],
  links: [],
  facts: [],
  steps: [],
  rules: [],
  pitfalls: [],
};

const EMPTY_FAQ: EcosystemFaqInput = {
  id: "",
  question: "",
  answer: "",
};

const EMPTY_MARKER: InfoMarkerItemInput = {
  markerKey: "",
  shortText: "",
  detailText: "",
  anchor: "/bilgilendirme",
};

const EMPTY_PAGE: EcosystemPagePayload = {
  title: "",
  subtitle: "",
  sections: [],
  faq: [],
};

const EMPTY_MARKERS: InfoMarkerPayload = {
  items: [],
};

const TOPIC_OPTIONS: TopicOption[] = [
  { key: "hesap", label: "Hesap" },
  { key: "okuma", label: "Okuma ve Erisim" },
  { key: "token", label: "Z-Token" },
  { key: "odeme", label: "Odeme ve Paketler" },
  { key: "creator", label: "Creator" },
  { key: "topluluk", label: "Topluluk" },
  { key: "profil", label: "Profil" },
  { key: "guvenlik", label: "Guvenlik" },
  { key: "admin", label: "Admin ve Finans" },
];

const LEGACY_SECTION_TOPIC_MAP: Record<string, string> = {
  auth: "hesap",
  quota: "okuma",
  "token-economy": "token",
  "creator-upload": "creator",
  progression: "topluluk",
  "comments-profile": "topluluk",
  "security-access": "guvenlik",
  "admin-finance": "admin",
};

const TOPIC_DEFAULT_LINKS: Record<string, Array<{ labelTr: string; labelEn: string; href: string }>> = {
  hesap: [
    { labelTr: "Giris", labelEn: "Login", href: "/login" },
    { labelTr: "Hesap durumu", labelEn: "Account status", href: "/account-status" },
  ],
  okuma: [
    { labelTr: "Kesfet", labelEn: "Explore", href: "/explore" },
    { labelTr: "Kutuphane", labelEn: "Library", href: "/library" },
  ],
  token: [
    { labelTr: "Z-Token", labelEn: "Z-Token", href: "/z-tokens" },
    { labelTr: "Paketler", labelEn: "Packages", href: "/packages" },
  ],
  odeme: [
    { labelTr: "Paketler", labelEn: "Packages", href: "/packages" },
    { labelTr: "Odeme", labelEn: "Payments", href: "/payments" },
  ],
  creator: [
    { labelTr: "Creator eserler", labelEn: "Creator works", href: "/creator/works" },
    { labelTr: "Creator basvuru", labelEn: "Creator submit", href: "/creator/submit" },
  ],
  topluluk: [
    { labelTr: "Kesfet", labelEn: "Explore", href: "/explore" },
    { labelTr: "Siralama", labelEn: "Rankings", href: "/rankings" },
  ],
  profil: [
    { labelTr: "Profil", labelEn: "Profile", href: "/profile" },
    { labelTr: "Kutuphane", labelEn: "Library", href: "/library" },
  ],
  guvenlik: [
    { labelTr: "Hesap", labelEn: "Account", href: "/profile" },
    { labelTr: "Sozlesmeler", labelEn: "Legal", href: "/legal" },
  ],
  admin: [
    { labelTr: "Destek", labelEn: "Support", href: "/feedback" },
    { labelTr: "Bilgilendirme", labelEn: "Information", href: "/bilgilendirme" },
  ],
};

type BaselineSeed = {
  topic: string;
  slug: string;
  titleTr: string;
  titleEn: string;
  summaryTr: string;
  summaryEn: string;
};

const BASELINE_SEEDS: BaselineSeed[] = [
  { topic: "hesap", slug: "giris-ve-dogrulama", titleTr: "Giris ve dogrulama", titleEn: "Login and verification", summaryTr: "Giris ve e-posta onay kurallari.", summaryEn: "Login and verification gate rules." },
  { topic: "hesap", slug: "sifre-ve-hesap-guvenligi", titleTr: "Sifre ve hesap guvenligi", titleEn: "Password and account safety", summaryTr: "Sifre sifirlama ve koruma davranisi.", summaryEn: "Password reset and account protection behavior." },
  { topic: "okuma", slug: "kota-ve-bolum-erisimi", titleTr: "Kota ve bolum erisimi", titleEn: "Quota and chapter access", summaryTr: "Ucretsiz/paket kota ve erisim sirasi.", summaryEn: "Free/package quota and access order." },
  { topic: "okuma", slug: "kitaplik-ve-devam", titleTr: "Kutuphane ve devam", titleEn: "Library and continue reading", summaryTr: "Okuma devamliligi ve tekrar acim.", summaryEn: "Reading continuity and reopen behavior." },
  { topic: "token", slug: "z-token-bakiye-ve-harcama", titleTr: "Z-Token bakiye ve harcama", titleEn: "Token balance and spending", summaryTr: "Bakiye hareketleri ve tekli unlock.", summaryEn: "Balance movements and single unlock flow." },
  { topic: "token", slug: "hediye-ve-iade", titleTr: "Hediye ve iade", titleEn: "Gift and refund", summaryTr: "Hediye ve iade kayit kurallari.", summaryEn: "Gift/refund recording rules." },
  { topic: "odeme", slug: "paket-satin-alma", titleTr: "Paket satin alma", titleEn: "Package purchase", summaryTr: "Satin alma baslangic ve callback sureci.", summaryEn: "Purchase start and callback finalize process." },
  { topic: "odeme", slug: "odeme-sonucu-ve-hata", titleTr: "Odeme sonucu ve hata", titleEn: "Payment result and failure", summaryTr: "Basari/hata ve tekrar deneme kurallari.", summaryEn: "Success/failure and retry rules." },
  { topic: "creator", slug: "yukleme-standartlari", titleTr: "Yukleme standartlari", titleEn: "Upload standards", summaryTr: "Hero/Cover/Tile/Banner oran ve limitleri.", summaryEn: "Hero/Cover/Tile/Banner ratio and limits." },
  { topic: "creator", slug: "sahiplik-ve-roller", titleTr: "Sahiplik ve roller", titleEn: "Ownership and roles", summaryTr: "Sahiplik devri ve yetki sinkronu.", summaryEn: "Ownership transfer and permission sync." },
  { topic: "topluluk", slug: "yorumlar-ve-etkilesim", titleTr: "Yorumlar ve etkilesim", titleEn: "Comments and interactions", summaryTr: "Yorum ve moderasyon davranisi.", summaryEn: "Comment and moderation behavior." },
  { topic: "topluluk", slug: "ilerleme-ve-basari", titleTr: "Ilerleme ve basari", titleEn: "Progress and achievements", summaryTr: "EXP ve basari adimlari.", summaryEn: "EXP and achievement milestones." },
  { topic: "profil", slug: "profil-ve-sosyal", titleTr: "Profil ve sosyal", titleEn: "Profile and social", summaryTr: "Profil gorunurlugu ve sosyal alanlar.", summaryEn: "Profile visibility and social fields." },
  { topic: "profil", slug: "favori-ve-kutuphane", titleTr: "Favori ve kutuphane", titleEn: "Favorites and library", summaryTr: "Kayitli icerik ve geri donus akisi.", summaryEn: "Saved content and return flow." },
  { topic: "guvenlik", slug: "oturum-ve-erisim", titleTr: "Oturum ve erisim", titleEn: "Session and access", summaryTr: "Oturum sinirlari ve yetki kontrolleri.", summaryEn: "Session boundaries and access checks." },
  { topic: "guvenlik", slug: "supheli-islem-ve-koruma", titleTr: "Supheli islem ve koruma", titleEn: "Suspicious actions and protection", summaryTr: "Riskli davranislarda koruma adimlari.", summaryEn: "Protection steps in risk scenarios." },
  { topic: "admin", slug: "finans-ve-destek", titleTr: "Finans ve destek", titleEn: "Finance and support", summaryTr: "Kullaniciyi etkileyen operasyonlar.", summaryEn: "User-facing operational flows." },
  { topic: "admin", slug: "icerik-ve-bilgilendirme-yonetimi", titleTr: "Icerik ve bilgilendirme yonetimi", titleEn: "Content and guide governance", summaryTr: "Taslak/yayin ve icerik kontrolu.", summaryEn: "Draft/publish and content governance." },
];

function buildBaselinePage(locale: EcosystemLocale): EcosystemPagePayload {
  const isTr = locale === "tr";
  return {
    title: isTr ? "Zenitoon Bilgilendirme" : "Zenitoon Guide",
    subtitle: isTr
      ? "Hesap, okuma, token, odeme, creator, topluluk, profil, guvenlik ve admin akislari tek merkezde."
      : "Single source for account, reading, token, payment, creator, community, profile, security and admin flows.",
    sections: BASELINE_SEEDS.map((seed) => {
      const links = TOPIC_DEFAULT_LINKS[seed.topic] ?? [];
      return {
        id: `${seed.topic}.${seed.slug}`,
        title: isTr ? seed.titleTr : seed.titleEn,
        summary: isTr ? seed.summaryTr : seed.summaryEn,
        body: isTr
          ? `<p>${seed.summaryTr}</p><p>Bu alan kullanicinin ilgili akis hakkinda dogru karar alabilmesi icin guncel kurallari aciklar.</p>`
          : `<p>${seed.summaryEn}</p><p>This section explains current rules so users can make correct decisions in the flow.</p>`,
        highlights: isTr ? ["Kapsam", "Kural", "Aksiyon"] : ["Scope", "Rule", "Action"],
        links: links.map((link) => ({ label: isTr ? link.labelTr : link.labelEn, href: link.href })),
        facts: isTr
          ? [
              { label: "Durum", value: "Aktif", hint: "Canli kural uygulanir" },
              { label: "Etki", value: "Kullanici", hint: "Arayuz davranisini etkiler" },
              { label: "Kaynak", value: "Sistem", hint: "Adminden yonetilir" },
            ]
          : [
              { label: "Status", value: "Active", hint: "Live policy is applied" },
              { label: "Impact", value: "User", hint: "Affects UI behavior" },
              { label: "Source", value: "System", hint: "Managed from admin" },
            ],
        steps: isTr ? ["Adim 1", "Adim 2", "Adim 3"] : ["Step 1", "Step 2", "Step 3"],
        rules: isTr ? ["Kural 1", "Kural 2", "Kural 3"] : ["Rule 1", "Rule 2", "Rule 3"],
        pitfalls: isTr ? ["Hata 1", "Hata 2"] : ["Pitfall 1", "Pitfall 2"],
      };
    }),
    faq: isTr
      ? [
          { id: "faq-1", question: "E-posta onayi olmadan ne yapabilirim?", answer: "<p>Giris yapabilirsin ancak satin alma adimlari kapali olur.</p>" },
          { id: "faq-2", question: "Kota bitince ne olur?", answer: "<p>Paket veya token fallback akisiyla devam edebilirsin.</p>" },
          { id: "faq-3", question: "Creator medya sonradan guncellenir mi?", answer: "<p>Evet, yetkili sahipler policy limitleri dahilinde guncelleyebilir.</p>" },
        ]
      : [
          { id: "faq-1", question: "What can I do before email verification?", answer: "<p>You can sign in, but purchase actions stay blocked.</p>" },
          { id: "faq-2", question: "What happens when quota is exhausted?", answer: "<p>You can continue with package rights or token fallback.</p>" },
          { id: "faq-3", question: "Can creator media be updated later?", answer: "<p>Yes, authorized owners can update media within policy limits.</p>" },
        ],
  };
}

function sanitizeRichHtml(value: string): string {
  return (value ?? "")
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, "")
    .replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, "")
    .replace(/\son\w+="[^"]*"/gi, "")
    .replace(/\son\w+='[^']*'/gi, "")
    .replace(/javascript:/gi, "");
}

function clonePage(page: EcosystemPagePayload): EcosystemPagePayload {
  return {
    title: page.title,
    subtitle: page.subtitle,
    sections: page.sections.map((s) => ({
      id: s.id,
      title: s.title,
      summary: s.summary,
      body: s.body,
      highlights: [...(s.highlights ?? [])],
      links: (s.links ?? []).map((l) => ({ label: l.label, href: l.href })),
      facts: (s.facts ?? []).map((fact) => ({ label: fact.label, value: fact.value, hint: fact.hint ?? "" })),
      steps: [...(s.steps ?? [])],
      rules: [...(s.rules ?? [])],
      pitfalls: [...(s.pitfalls ?? [])],
    })),
    faq: page.faq.map((q) => ({ id: q.id, question: q.question, answer: q.answer })),
  };
}

function tabButtonClass(active: boolean): string {
  return active
    ? "rounded-lg border border-emerald-500 bg-emerald-500 px-3 py-1.5 text-xs font-semibold text-slate-950"
    : "rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50";
}

function slugify(value: string): string {
  return (value ?? "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function resolveTopicAndSlug(sectionId: string): { topicKey: string; subtopicSlug: string } {
  const normalized = (sectionId ?? "").trim();
  if (!normalized) {
    return { topicKey: "hesap", subtopicSlug: "" };
  }

  const dotIndex = normalized.indexOf(".");
  if (dotIndex > 0) {
    const topicKey = normalized.slice(0, dotIndex).trim().toLowerCase();
    const subtopicSlug = normalized.slice(dotIndex + 1).trim();
    const known = TOPIC_OPTIONS.some((item) => item.key === topicKey);
    if (known) {
      return { topicKey, subtopicSlug };
    }
  }

  return {
    topicKey: LEGACY_SECTION_TOPIC_MAP[normalized] ?? "hesap",
    subtopicSlug: normalized,
  };
}

function buildSectionId(topicKey: string, subtopicSlug: string): string {
  const safeTopic = slugify(topicKey);
  const safeSubtopic = slugify(subtopicSlug);
  if (!safeTopic && !safeSubtopic) {
    return "";
  }
  if (!safeTopic) {
    return safeSubtopic;
  }
  if (!safeSubtopic) {
    return safeTopic;
  }
  return `${safeTopic}.${safeSubtopic}`;
}

function topicLabel(topicKey: string): string {
  return TOPIC_OPTIONS.find((item) => item.key === topicKey)?.label ?? topicKey;
}

function parseFacts(value: string): EcosystemFactInput[] {
  return value
    .split("|")
    .map((token) => token.trim())
    .filter(Boolean)
    .map((token) => {
      const [labelRaw, valueRaw, hintRaw] = token.split("=>");
      return {
        label: (labelRaw ?? "").trim(),
        value: (valueRaw ?? "").trim(),
        hint: (hintRaw ?? "").trim(),
      };
    })
    .filter((item) => item.label.length > 0 && item.value.length > 0);
}

function serializeFacts(facts: EcosystemFactInput[] | undefined): string {
  if (!Array.isArray(facts) || facts.length === 0) {
    return "";
  }
  return facts
    .map((fact) => `${fact.label}=>${fact.value}${fact.hint ? `=>${fact.hint}` : ""}`)
    .join(" | ");
}

function topicTemplate(topicKey: string, locale: EcosystemLocale): EcosystemSectionInput {
  const tr = locale === "tr";
  const titleMap: Record<string, string> = {
    hesap: tr ? "Hesap Akisi" : "Account Flow",
    okuma: tr ? "Okuma Akisi" : "Reading Flow",
    token: tr ? "Z-Token Akisi" : "Z-Token Flow",
    odeme: tr ? "Odeme Akisi" : "Payment Flow",
    creator: tr ? "Creator Akisi" : "Creator Flow",
    topluluk: tr ? "Topluluk Akisi" : "Community Flow",
    profil: tr ? "Profil Akisi" : "Profile Flow",
    guvenlik: tr ? "Guvenlik Akisi" : "Security Flow",
    admin: tr ? "Operasyon Akisi" : "Operations Flow",
  };

  return {
    id: `${topicKey}.yeni-baslik`,
    title: titleMap[topicKey] ?? (tr ? "Bilgilendirme Basligi" : "Guide Section"),
    summary: tr ? "Bu baslik altinda kullanicinin bilmesi gereken temel akislar yer alir." : "This section explains core user-facing flow details.",
    body: tr ? "<p>Bu alana konu aciklamasini net ve kisa sekilde yaz.</p>" : "<p>Write concise and clear topic guidance here.</p>",
    highlights: tr ? ["Temel bilgi", "Akis", "Kritik not"] : ["Core detail", "Flow", "Critical note"],
    links: tr ? [{ label: "Detay sayfasi", href: "/bilgilendirme" }] : [{ label: "Details page", href: "/bilgilendirme" }],
    facts: tr
      ? [
          { label: "Durum", value: "Aktif", hint: "Sistem akisi acik" },
          { label: "Etki", value: "Kullanici", hint: "Arayuz davranisini etkiler" },
          { label: "Kaynak", value: "Kural", hint: "Yonetilebilir kural" },
        ]
      : [
          { label: "Status", value: "Active", hint: "Flow is enabled" },
          { label: "Impact", value: "User", hint: "Affects UI behavior" },
          { label: "Source", value: "Policy", hint: "Managed rule" },
        ],
    steps: tr ? ["Adim 1", "Adim 2", "Adim 3"] : ["Step 1", "Step 2", "Step 3"],
    rules: tr ? ["Kural 1", "Kural 2", "Kural 3"] : ["Rule 1", "Rule 2", "Rule 3"],
    pitfalls: tr ? ["Hata 1", "Hata 2"] : ["Pitfall 1", "Pitfall 2"],
  };
}

function ensureUniqueSectionId(candidateId: string, existingSections: EcosystemSectionInput[]): string {
  const base = (candidateId ?? "").trim().toLowerCase();
  if (!base) {
    return candidateId;
  }

  const existing = new Set(existingSections.map((item) => (item.id ?? "").trim().toLowerCase()).filter(Boolean));
  if (!existing.has(base)) {
    return candidateId;
  }

  let index = 2;
  let nextId = `${base}-${index}`;
  while (existing.has(nextId)) {
    index += 1;
    nextId = `${base}-${index}`;
  }

  return nextId;
}

function validatePageDraft(page: EcosystemPagePayload): string[] {
  const errors: string[] = [];
  const sections = page.sections ?? [];
  const ids = new Set<string>();
  const topicStats = new Map<string, { sections: number; facts: number; rules: number; pitfalls: number; links: number }>();

  for (let index = 0; index < sections.length; index += 1) {
    const section = sections[index];
    const label = section.title || `Kategori ${index + 1}`;
    if (!section.id?.trim()) errors.push(`${label}: id bos olamaz.`);
    if (!section.title?.trim()) errors.push(`Section ${index + 1}: baslik bos olamaz.`);
    if (!section.summary?.trim()) errors.push(`${label}: ozet bos olamaz.`);
    if (!section.body?.trim()) errors.push(`${label}: icerik bos olamaz.`);

    const normalizedId = section.id?.trim().toLowerCase() ?? "";
    if (normalizedId) {
      if (ids.has(normalizedId)) {
        errors.push(`${label}: yinelenen id (${section.id}).`);
      }
      ids.add(normalizedId);

      if (!normalizedId.includes(".")) {
        errors.push(`${label}: id 'topic.subtopic' formatinda olmali.`);
      } else {
        const topicKey = normalizedId.split(".")[0];
        if (!TOPIC_OPTIONS.some((item) => item.key === topicKey)) {
          errors.push(`${label}: gecersiz topic prefix (${topicKey}).`);
        } else {
          const current = topicStats.get(topicKey) ?? { sections: 0, facts: 0, rules: 0, pitfalls: 0, links: 0 };
          current.sections += 1;
          current.facts += Array.isArray(section.facts) ? section.facts.filter((item) => item.label?.trim() && item.value?.trim()).length : 0;
          current.rules += Array.isArray(section.rules) ? section.rules.filter((item) => (item ?? "").trim()).length : 0;
          current.pitfalls += Array.isArray(section.pitfalls) ? section.pitfalls.filter((item) => (item ?? "").trim()).length : 0;
          current.links += Array.isArray(section.links) ? section.links.filter((item) => item.label?.trim() && item.href?.trim()).length : 0;
          topicStats.set(topicKey, current);
        }
      }
    }
  }

  for (const topic of TOPIC_OPTIONS) {
    const stats = topicStats.get(topic.key) ?? { sections: 0, facts: 0, rules: 0, pitfalls: 0, links: 0 };
    if (stats.sections < 2) errors.push(`${topic.label}: en az 2 subtopic olmali.`);
    if (stats.facts < 3) errors.push(`${topic.label}: toplam en az 3 bilgi karti olmali.`);
    if (stats.rules < 3) errors.push(`${topic.label}: toplam en az 3 kural olmali.`);
    if (stats.pitfalls < 2) errors.push(`${topic.label}: toplam en az 2 sik hata maddesi olmali.`);
    if (stats.links < 2) errors.push(`${topic.label}: toplam en az 2 yonlendirme linki olmali.`);
  }

  return errors;
}

export default function AdminEcosystemContent() {
  const [locale, setLocale] = useState<EcosystemLocale>("tr");
  const [tab, setTab] = useState<AdminTabKey>("overview");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [pageDraft, setPageDraft] = useState<EcosystemPagePayload>(EMPTY_PAGE);
  const [markersDraft, setMarkersDraft] = useState<InfoMarkerPayload>(EMPTY_MARKERS);
  const [pagePublished, setPagePublished] = useState<EcosystemPagePayload>(EMPTY_PAGE);
  const [markersPublished, setMarkersPublished] = useState<InfoMarkerPayload>(EMPTY_MARKERS);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [publishedAt, setPublishedAt] = useState<string | null>(null);
  const [activeSectionIndex, setActiveSectionIndex] = useState(0);
  const [activeFaqIndex, setActiveFaqIndex] = useState(0);
  const [activeMarkerIndex, setActiveMarkerIndex] = useState(0);
  const [templateTopic, setTemplateTopic] = useState<string>("hesap");

  const localeLabel = useMemo(() => (locale === "tr" ? "Turkce" : "English"), [locale]);
  const publishDiffSummary = useMemo(() => {
    const draftSections = pageDraft.sections ?? [];
    const pubSections = pagePublished.sections ?? [];
    const draftMap = new Map(draftSections.map((section) => [section.id, JSON.stringify(section)]));
    const pubMap = new Map(pubSections.map((section) => [section.id, JSON.stringify(section)]));

    let sectionChanged = 0;
    draftMap.forEach((value, id) => {
      if (!pubMap.has(id) || pubMap.get(id) !== value) {
        sectionChanged += 1;
      }
    });

    const draftFaq = pageDraft.faq ?? [];
    const pubFaq = pagePublished.faq ?? [];
    const faqChanged = JSON.stringify(draftFaq) === JSON.stringify(pubFaq) ? 0 : Math.abs(draftFaq.length - pubFaq.length) + 1;

    const markerChanged = JSON.stringify(markersDraft.items ?? []) === JSON.stringify(markersPublished.items ?? [])
      ? 0
      : Math.abs((markersDraft.items ?? []).length - (markersPublished.items ?? []).length) + 1;

    return { sectionChanged, faqChanged, markerChanged };
  }, [pageDraft, pagePublished, markersDraft, markersPublished]);

  const load = async (selectedLocale: EcosystemLocale) => {
    setLoading(true);
    try {
      const [page, markers] = await Promise.all([
        getAdminEcosystemPage(selectedLocale),
        getAdminEcosystemMarkers(selectedLocale),
      ]);
      setPageDraft(page.draft ?? EMPTY_PAGE);
      setMarkersDraft(markers.draft ?? EMPTY_MARKERS);
      setPagePublished(page.published ?? EMPTY_PAGE);
      setMarkersPublished(markers.published ?? EMPTY_MARKERS);
      setUpdatedAt(page.updatedAtUtc ?? markers.updatedAtUtc ?? null);
      setPublishedAt(page.publishedAtUtc ?? markers.publishedAtUtc ?? null);
      setActiveSectionIndex(0);
      setActiveFaqIndex(0);
      setActiveMarkerIndex(0);
    } catch (error) {
      console.error("[admin/bilgilendirme] load failed", error);
      await Swal.fire("Hata", "Bilgilendirme icerigi yuklenemedi.", "error");
      setPageDraft(EMPTY_PAGE);
      setMarkersDraft(EMPTY_MARKERS);
      setPagePublished(EMPTY_PAGE);
      setMarkersPublished(EMPTY_MARKERS);
      setUpdatedAt(null);
      setPublishedAt(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load(locale);
  }, [locale]);

  const handleSave = async () => {
    const errors = validatePageDraft(pageDraft);
    if (errors.length > 0) {
      await Swal.fire({
        title: "Dogrulama hatasi",
        icon: "error",
        html: `<ul style="text-align:left">${errors.map((item) => `<li>${item}</li>`).join("")}</ul>`,
      });
      return;
    }

    setSaving(true);
    try {
      const [pageOk, markerOk] = await Promise.all([
        saveAdminEcosystemPage(locale, pageDraft),
        saveAdminEcosystemMarkers(locale, markersDraft),
      ]);
      if (!pageOk || !markerOk) {
        await Swal.fire("Hata", "Taslak kaydedilemedi.", "error");
        return;
      }
      await Swal.fire("Basarili", "Taslak kaydedildi.", "success");
      await load(locale);
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    const errors = validatePageDraft(pageDraft);
    if (errors.length > 0) {
      await Swal.fire({
        title: "Dogrulama hatasi",
        icon: "error",
        html: `<ul style="text-align:left">${errors.map((item) => `<li>${item}</li>`).join("")}</ul>`,
      });
      return;
    }

    const confirm = await Swal.fire({
      title: "Yayinlansin mi?",
      html: `${localeLabel} taslak icerigi canliya alinacak.<br/><br/>Degisen section: <b>${publishDiffSummary.sectionChanged}</b><br/>Degisen SSS: <b>${publishDiffSummary.faqChanged}</b><br/>Degisen marker: <b>${publishDiffSummary.markerChanged}</b>`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yayinla",
      cancelButtonText: "Vazgec",
    });
    if (!confirm.isConfirmed) return;

    setPublishing(true);
    try {
      const ok = await publishAdminEcosystem(locale);
      if (!ok) {
        await Swal.fire("Hata", "Yayinlama basarisiz.", "error");
        return;
      }
      await Swal.fire("Basarili", "Bilgilendirme yayinlandi.", "success");
      await load(locale);
    } finally {
      setPublishing(false);
    }
  };

  const applyBaseline = async () => {
    const confirm = await Swal.fire({
      title: "Temiz taslak yuklensin mi?",
      text: "Mevcut form degisiklikleri yeni baseline ile degisir (kaydetmeden DB'ye yazilmaz).",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yukle",
      cancelButtonText: "Vazgec",
    });
    if (!confirm.isConfirmed) {
      return;
    }

    const baseline = buildBaselinePage(locale);
    setPageDraft(clonePage(baseline));
    setTab("categories");
    setActiveSectionIndex(0);
  };

  const sections = useMemo(() => pageDraft.sections ?? [], [pageDraft.sections]);
  const faq = useMemo(() => pageDraft.faq ?? [], [pageDraft.faq]);
  const markers = useMemo(() => markersDraft.items ?? [], [markersDraft.items]);
  const groupedSections = useMemo(() => {
    const map = new Map<string, Array<{ section: EcosystemSectionInput; index: number }>>();
    for (let index = 0; index < sections.length; index += 1) {
      const section = sections[index];
      const topicKey = resolveTopicAndSlug(section.id).topicKey;
      if (!map.has(topicKey)) {
        map.set(topicKey, []);
      }
      map.get(topicKey)!.push({ section, index });
    }
    return map;
  }, [sections]);
  const activeSection = sections[activeSectionIndex] ?? null;
  const activeFaq = faq[activeFaqIndex] ?? null;
  const activeMarker = markers[activeMarkerIndex] ?? null;
  const activeSectionMeta = activeSection ? resolveTopicAndSlug(activeSection.id) : null;

  const setSection = (next: EcosystemSectionInput) => {
    setPageDraft((prev) => ({
      ...prev,
      sections: prev.sections.map((item, index) => (index === activeSectionIndex ? next : item)),
    }));
  };

  const setFaqItem = (next: EcosystemFaqInput) => {
    setPageDraft((prev) => ({
      ...prev,
      faq: prev.faq.map((item, index) => (index === activeFaqIndex ? next : item)),
    }));
  };

  const setMarkerItem = (next: InfoMarkerItemInput) => {
    setMarkersDraft((prev) => ({
      ...prev,
      items: prev.items.map((item, index) => (index === activeMarkerIndex ? next : item)),
    }));
  };

  return (
    <div className="space-y-4">
      <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Bilgilendirme Icerik Yonetimi</h2>
            <p className="text-sm text-slate-600">Kategori bazli public bilgilendirme sayfasi icerikleri buradan yonetilir.</p>
            <p className="mt-1 text-xs text-slate-500">
              Son guncelleme: {updatedAt ? new Date(updatedAt).toLocaleString("tr-TR") : "yok"} - Son yayinlama:{" "}
              {publishedAt ? new Date(publishedAt).toLocaleString("tr-TR") : "yok"}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={locale}
              onChange={(event) => setLocale(event.target.value as EcosystemLocale)}
              className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700"
            >
              <option value="tr">TR</option>
              <option value="en">EN</option>
            </select>
            <button
              type="button"
              onClick={() => void load(locale)}
              className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              disabled={loading}
            >
              Yenile
            </button>
            <button
              type="button"
              onClick={() => void applyBaseline()}
              className="rounded-lg border border-cyan-300 bg-cyan-50 px-3 py-2 text-sm font-semibold text-cyan-800 hover:bg-cyan-100"
              disabled={loading}
            >
              Temiz Taslak
            </button>
            <button
              type="button"
              onClick={() => void handleSave()}
              className="rounded-lg border border-slate-900 bg-slate-900 px-3 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-70"
              disabled={loading || saving}
            >
              {saving ? "Kaydediliyor..." : "Taslak Kaydet"}
            </button>
            <button
              type="button"
              onClick={() => void handlePublish()}
              className="rounded-lg border border-emerald-500 bg-emerald-500 px-3 py-2 text-sm font-semibold text-slate-950 hover:bg-emerald-400 disabled:opacity-70"
              disabled={loading || publishing}
            >
              {publishing ? "Yayinlaniyor..." : "Yayinla"}
            </button>
          </div>
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-wrap gap-2">
          <button type="button" className={tabButtonClass(tab === "overview")} onClick={() => setTab("overview")}>Sayfa</button>
          <button type="button" className={tabButtonClass(tab === "categories")} onClick={() => setTab("categories")}>Kategoriler</button>
          <button type="button" className={tabButtonClass(tab === "faq")} onClick={() => setTab("faq")}>SSS</button>
          <button type="button" className={tabButtonClass(tab === "markers")} onClick={() => setTab("markers")}>Bilgi Etiketleri</button>
          <button type="button" className={tabButtonClass(tab === "preview")} onClick={() => setTab("preview")}>Canli Onizleme</button>
        </div>
      </section>

      {loading ? (
        <section className="rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-600 shadow-sm">
          Bilgilendirme icerigi yukleniyor...
        </section>
      ) : null}

      {!loading && tab === "overview" ? (
        <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <h3 className="text-base font-semibold text-slate-900">Sayfa Basligi</h3>
          <div className="mt-3 grid gap-3 md:grid-cols-2">
            <label className="space-y-1 text-sm">
              <span className="text-slate-700">Baslik</span>
              <input
                value={pageDraft.title}
                onChange={(event) => setPageDraft((prev) => ({ ...prev, title: event.target.value }))}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              />
            </label>
            <label className="space-y-1 text-sm md:col-span-2">
              <span className="text-slate-700">Alt baslik</span>
              <textarea
                value={pageDraft.subtitle}
                onChange={(event) => setPageDraft((prev) => ({ ...prev, subtitle: event.target.value }))}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                rows={3}
              />
            </label>
          </div>
        </section>
      ) : null}

      {!loading && tab === "categories" ? (
        <section className="grid gap-4 lg:grid-cols-[320px_minmax(0,1fr)_360px]">
          <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-900">Kategori Listesi</h3>
              <button
                type="button"
                className="rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700"
                onClick={() => {
                  setPageDraft((prev) => ({ ...prev, sections: [...prev.sections, { ...EMPTY_SECTION }] }));
                  setActiveSectionIndex(sections.length);
                }}
              >
                Ekle
              </button>
            </div>
            <div className="mb-3 flex items-center gap-2">
              <select
                value={templateTopic}
                onChange={(event) => setTemplateTopic(event.target.value)}
                className="flex-1 rounded-lg border border-slate-300 px-2 py-1.5 text-xs"
              >
                {TOPIC_OPTIONS.map((topic) => (
                  <option key={topic.key} value={topic.key}>
                    {topic.label}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="rounded-lg border border-cyan-300 bg-cyan-50 px-2.5 py-1.5 text-xs font-semibold text-cyan-800 hover:bg-cyan-100"
                onClick={() => {
                  const template = topicTemplate(templateTopic, locale);
                  setPageDraft((prev) => {
                    const uniqueId = ensureUniqueSectionId(template.id, prev.sections);
                    return { ...prev, sections: [...prev.sections, { ...template, id: uniqueId }] };
                  });
                  setActiveSectionIndex(sections.length);
                }}
              >
                Sablon Ekle
              </button>
            </div>
            <div className="space-y-3">
              {TOPIC_OPTIONS.map((topic) => {
                const topicItems = groupedSections.get(topic.key) ?? [];
                if (topicItems.length === 0) return null;
                return (
                  <details key={topic.key} open className="rounded-xl border border-slate-200 bg-slate-50">
                    <summary className="cursor-pointer list-none px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-700">
                      {topic.label}
                    </summary>
                    <div className="space-y-1 border-t border-slate-200 p-2">
                      {topicItems.map(({ section, index }) => {
                        const active = index === activeSectionIndex;
                        return (
                          <button
                            key={`${section.id}-${index}`}
                            type="button"
                            onClick={() => setActiveSectionIndex(index)}
                            className={`w-full rounded-lg border px-3 py-2 text-left ${
                              active ? "border-emerald-400 bg-emerald-50" : "border-slate-200 bg-white"
                            }`}
                          >
                            <p className="text-xs font-semibold text-slate-900">{section.title || `Kategori ${index + 1}`}</p>
                            <p className="mt-1 text-[11px] text-slate-600">{section.id || "id yok"}</p>
                          </button>
                        );
                      })}
                    </div>
                  </details>
                );
              })}
              {sections.length === 0 ? <p className="text-xs text-slate-500">Kategori yok.</p> : null}
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            {!activeSection ? (
              <p className="text-sm text-slate-500">Duzenlemek icin soldan bir kategori sec.</p>
            ) : (
              <div className="space-y-3">
                <div className="flex justify-end">
                  <button
                    type="button"
                    className="rounded border border-rose-300 px-2 py-1 text-[11px] text-rose-700"
                    onClick={() => {
                      setPageDraft((prev) => ({ ...prev, sections: prev.sections.filter((_, i) => i !== activeSectionIndex) }));
                      setActiveSectionIndex((prev) => Math.max(0, prev - 1));
                    }}
                  >
                    Kategoriyi Sil
                  </button>
                </div>
                <div className="grid gap-2 md:grid-cols-2">
                  <select
                    value={activeSectionMeta?.topicKey ?? "hesap"}
                    onChange={(event) => {
                      const nextTopic = event.target.value;
                      const nextId = buildSectionId(nextTopic, activeSectionMeta?.subtopicSlug ?? activeSection.id);
                      setSection({ ...activeSection, id: nextId });
                    }}
                    className="rounded border border-slate-300 px-3 py-2 text-sm"
                  >
                    {TOPIC_OPTIONS.map((topic) => (
                      <option key={topic.key} value={topic.key}>
                        {topic.label}
                      </option>
                    ))}
                  </select>
                  <input
                    value={activeSectionMeta?.subtopicSlug ?? ""}
                    onChange={(event) => {
                      const nextSlug = slugify(event.target.value);
                      const nextId = buildSectionId(activeSectionMeta?.topicKey ?? "hesap", nextSlug);
                      setSection({ ...activeSection, id: nextId });
                    }}
                    placeholder="Alt konu slug (ornek: yukleme-limitleri)"
                    className="rounded border border-slate-300 px-3 py-2 text-sm"
                  />
                  <input
                    value={activeSection.id}
                    readOnly
                    className="rounded border border-slate-200 bg-slate-100 px-3 py-2 text-sm text-slate-600 md:col-span-2"
                  />
                  <input
                    value={activeSection.title}
                    onChange={(event) => setSection({ ...activeSection, title: event.target.value })}
                    placeholder="Kategori basligi"
                    className="rounded border border-slate-300 px-3 py-2 text-sm"
                  />
                </div>
                <p className="text-xs text-slate-500">
                  Konu: <span className="font-semibold text-slate-700">{topicLabel(activeSectionMeta?.topicKey ?? "hesap")}</span>
                </p>
                <input
                  value={activeSection.summary}
                  onChange={(event) => setSection({ ...activeSection, summary: event.target.value })}
                  placeholder="Kisa ozet"
                  className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
                />
                <RichTextEditor
                  value={activeSection.body}
                  onChange={(nextHtml) => setSection({ ...activeSection, body: nextHtml })}
                  minHeight={220}
                  placeholder="Kategori detay metni"
                />
                <input
                  value={(activeSection.highlights ?? []).join(" | ")}
                  onChange={(event) =>
                    setSection({
                      ...activeSection,
                      highlights: event.target.value.split("|").map((v) => v.trim()).filter(Boolean),
                    })
                  }
                  placeholder="One cikan maddeler (| ile ayir)"
                  className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
                />
                <input
                  value={(activeSection.links ?? []).map((link) => `${link.label}=>${link.href}`).join(" | ")}
                  onChange={(event) =>
                    setSection({
                      ...activeSection,
                      links: event.target.value
                        .split("|")
                        .map((token) => token.trim())
                        .filter(Boolean)
                        .map((token) => {
                          const [label, href] = token.split("=>");
                          return { label: (label ?? "").trim(), href: (href ?? "").trim() };
                        })
                        .filter((link) => link.label && link.href),
                    })
                  }
                  placeholder="Linkler (Etiket=>/hedef | ...)"
                  className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
                />
                <input
                  value={serializeFacts(activeSection.facts)}
                  onChange={(event) =>
                    setSection({
                      ...activeSection,
                      facts: parseFacts(event.target.value),
                    })
                  }
                  placeholder="Facts (Etiket=>Deger=>Ipucu | ...)"
                  className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
                />
                <textarea
                  value={(activeSection.steps ?? []).join("\n")}
                  onChange={(event) =>
                    setSection({
                      ...activeSection,
                      steps: event.target.value.split("\n").map((item) => item.trim()).filter(Boolean),
                    })
                  }
                  placeholder="Nasil calisir adimlari (her satir bir adim)"
                  className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
                  rows={3}
                />
                <textarea
                  value={(activeSection.rules ?? []).join("\n")}
                  onChange={(event) =>
                    setSection({
                      ...activeSection,
                      rules: event.target.value.split("\n").map((item) => item.trim()).filter(Boolean),
                    })
                  }
                  placeholder="Kurallar (her satir bir kural)"
                  className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
                  rows={3}
                />
                <textarea
                  value={(activeSection.pitfalls ?? []).join("\n")}
                  onChange={(event) =>
                    setSection({
                      ...activeSection,
                      pitfalls: event.target.value.split("\n").map((item) => item.trim()).filter(Boolean),
                    })
                  }
                  placeholder="Sik hatalar (her satir bir madde)"
                  className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
                  rows={3}
                />
              </div>
            )}
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <h4 className="text-sm font-semibold text-slate-900">Canli Bolum Onizleme</h4>
            {!activeSection ? (
              <p className="mt-3 text-sm text-slate-500">Soldan bir kategori sec.</p>
            ) : (
              <div className="mt-3 space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-slate-500">{activeSection.id}</p>
                  <h5 className="text-base font-semibold text-slate-900">{activeSection.title || "Baslik"}</h5>
                  <p className="text-sm text-slate-700">{activeSection.summary || "Ozet"}</p>
                </div>

                {(activeSection.facts ?? []).length > 0 ? (
                  <div className="grid gap-2">
                    {(activeSection.facts ?? []).map((fact, idx) => (
                      <div key={`${fact.label}-${idx}`} className="rounded-lg border border-slate-200 bg-white px-2 py-1.5">
                        <p className="text-[10px] uppercase tracking-wide text-slate-500">{fact.label}</p>
                        <p className="text-sm font-semibold text-slate-900">{fact.value}</p>
                        {fact.hint ? <p className="text-xs text-slate-600">{fact.hint}</p> : null}
                      </div>
                    ))}
                  </div>
                ) : null}

                {(activeSection.steps ?? []).length > 0 ? (
                  <div className="rounded-lg border border-slate-200 bg-white p-2">
                    <p className="text-xs font-semibold text-slate-800">Nasil calisir</p>
                    <ol className="mt-1 list-decimal space-y-1 pl-4 text-xs text-slate-700">
                      {(activeSection.steps ?? []).map((step) => (
                        <li key={step}>{step}</li>
                      ))}
                    </ol>
                  </div>
                ) : null}

                {(activeSection.rules ?? []).length > 0 ? (
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-2">
                    <p className="text-xs font-semibold text-emerald-800">Kurallar</p>
                    <ul className="mt-1 list-disc space-y-1 pl-4 text-xs text-emerald-900">
                      {(activeSection.rules ?? []).map((rule) => (
                        <li key={rule}>{rule}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                {(activeSection.pitfalls ?? []).length > 0 ? (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-2">
                    <p className="text-xs font-semibold text-amber-800">Sik hatalar</p>
                    <ul className="mt-1 list-disc space-y-1 pl-4 text-xs text-amber-900">
                      {(activeSection.pitfalls ?? []).map((pitfall) => (
                        <li key={pitfall}>{pitfall}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                <div
                  className="prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(activeSection.body || "") }}
                />
              </div>
            )}
          </div>
        </section>
      ) : null}

      {!loading && tab === "faq" ? (
        <section className="grid gap-4 lg:grid-cols-[320px_minmax(0,1fr)]">
          <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-900">SSS Listesi</h3>
              <button
                type="button"
                className="rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700"
                onClick={() => {
                  setPageDraft((prev) => ({ ...prev, faq: [...prev.faq, { ...EMPTY_FAQ }] }));
                  setActiveFaqIndex(faq.length);
                }}
              >
                Ekle
              </button>
            </div>
            <div className="space-y-2">
              {faq.map((item, index) => {
                const active = index === activeFaqIndex;
                return (
                  <button
                    key={`${item.id}-${index}`}
                    type="button"
                    onClick={() => setActiveFaqIndex(index)}
                    className={`w-full rounded-xl border px-3 py-2 text-left ${
                      active ? "border-emerald-400 bg-emerald-50" : "border-slate-200 bg-slate-50"
                    }`}
                  >
                    <p className="text-xs font-semibold text-slate-900">{item.question || `Soru ${index + 1}`}</p>
                    <p className="mt-1 text-[11px] text-slate-600">{item.id || "id yok"}</p>
                  </button>
                );
              })}
              {faq.length === 0 ? <p className="text-xs text-slate-500">SSS yok.</p> : null}
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            {!activeFaq ? (
              <p className="text-sm text-slate-500">Duzenlemek icin soldan bir soru sec.</p>
            ) : (
              <div className="space-y-3">
                <div className="flex justify-end">
                  <button
                    type="button"
                    className="rounded border border-rose-300 px-2 py-1 text-[11px] text-rose-700"
                    onClick={() => {
                      setPageDraft((prev) => ({ ...prev, faq: prev.faq.filter((_, i) => i !== activeFaqIndex) }));
                      setActiveFaqIndex((prev) => Math.max(0, prev - 1));
                    }}
                  >
                    Soruyu Sil
                  </button>
                </div>
                <input
                  value={activeFaq.id}
                  onChange={(event) => setFaqItem({ ...activeFaq, id: event.target.value })}
                  placeholder="id"
                  className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
                />
                <input
                  value={activeFaq.question}
                  onChange={(event) => setFaqItem({ ...activeFaq, question: event.target.value })}
                  placeholder="Soru"
                  className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
                />
                <RichTextEditor
                  value={activeFaq.answer}
                  onChange={(nextHtml) => setFaqItem({ ...activeFaq, answer: nextHtml })}
                  minHeight={160}
                  placeholder="Yanit"
                />
              </div>
            )}
          </div>
        </section>
      ) : null}

      {!loading && tab === "markers" ? (
        <section className="grid gap-4 lg:grid-cols-[320px_minmax(0,1fr)]">
          <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-900">Marker Listesi</h3>
              <button
                type="button"
                className="rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700"
                onClick={() => {
                  setMarkersDraft((prev) => ({ ...prev, items: [...prev.items, { ...EMPTY_MARKER }] }));
                  setActiveMarkerIndex(markers.length);
                }}
              >
                Ekle
              </button>
            </div>
            <div className="space-y-2">
              {markers.map((item, index) => {
                const active = index === activeMarkerIndex;
                return (
                  <button
                    key={`${item.markerKey}-${index}`}
                    type="button"
                    onClick={() => setActiveMarkerIndex(index)}
                    className={`w-full rounded-xl border px-3 py-2 text-left ${
                      active ? "border-emerald-400 bg-emerald-50" : "border-slate-200 bg-slate-50"
                    }`}
                  >
                    <p className="text-xs font-semibold text-slate-900">{item.shortText || `Bilgi etiketi ${index + 1}`}</p>
                    <p className="mt-1 text-[11px] text-slate-600">{item.markerKey || "key yok"}</p>
                  </button>
                );
              })}
              {markers.length === 0 ? <p className="text-xs text-slate-500">Bilgi etiketi yok.</p> : null}
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            {!activeMarker ? (
              <p className="text-sm text-slate-500">Duzenlemek icin soldan bir marker sec.</p>
            ) : (
              <div className="space-y-3">
                <div className="flex justify-end">
                  <button
                    type="button"
                    className="rounded border border-rose-300 px-2 py-1 text-[11px] text-rose-700"
                    onClick={() => {
                      setMarkersDraft((prev) => ({ ...prev, items: prev.items.filter((_, i) => i !== activeMarkerIndex) }));
                      setActiveMarkerIndex((prev) => Math.max(0, prev - 1));
                    }}
                  >
                    Marker Sil
                  </button>
                </div>
                <input
                  value={activeMarker.markerKey}
                  onChange={(event) => setMarkerItem({ ...activeMarker, markerKey: event.target.value })}
                  placeholder="Etiket anahtari (orn: economy.quota)"
                  className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
                />
                <input
                  value={activeMarker.shortText}
                  onChange={(event) => setMarkerItem({ ...activeMarker, shortText: event.target.value })}
                  placeholder="Kisa metin"
                  className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
                />
                <textarea
                  value={activeMarker.detailText}
                  onChange={(event) => setMarkerItem({ ...activeMarker, detailText: event.target.value })}
                  placeholder="Detay metni"
                  className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
                  rows={3}
                />
                <input
                  value={activeMarker.anchor}
                  onChange={(event) => setMarkerItem({ ...activeMarker, anchor: event.target.value })}
                  placeholder="/bilgilendirme#anchor"
                  className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
                />
              </div>
            )}
          </div>
        </section>
      ) : null}

      {!loading && tab === "preview" ? (
        <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <h3 className="text-base font-semibold text-slate-900">Canli Onizleme</h3>
          <div className="mt-4 space-y-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <div>
              <p className="text-xs uppercase tracking-[0.18em] text-slate-500">Bilgilendirme</p>
              <h4 className="mt-1 text-2xl font-semibold text-slate-900">{pageDraft.title || "Baslik yok"}</h4>
              <p className="mt-1 text-sm text-slate-700">{pageDraft.subtitle || "Alt baslik yok"}</p>
            </div>

            {sections.map((section) => (
              <article key={section.id || Math.random()} className="rounded-xl border border-slate-200 bg-white p-4">
                <p className="text-[11px] uppercase tracking-wide text-slate-500">{section.id || "id"}</p>
                <h5 className="mt-1 text-lg font-semibold text-slate-900">{section.title || "Baslik"}</h5>
                <p className="mt-1 text-sm text-slate-700">{section.summary || "Ozet"}</p>
                <div
                  className="prose mt-2 max-w-none prose-sm"
                  dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(section.body || "") }}
                />
              </article>
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}

